import { CartItem } from "./CartItem";
import { BigNumber } from "bignumber.js"
import { CurrentDateTimeInfo } from "../menu/CurrentDateTimeInfo";

export class CartObject {
    static readonly TIP_FEE_NAME = "tip";

    subtotal: number;
    tax: number;
    total: number;
    restaurantFees: { title: string, value: number }[];
    discounts: { title: string, value: number }[]
    cartItems: Map<string, CartItem>;//key = cartItemId, value is the carItem itself

    usedCouponCode:string|undefined;
    couponDiscountTitle: string | undefined;
    percentageDiscount: { title: string, percentage: number } | undefined;
    rewardPointInfo:CouponRewardPointInfo | undefined;

    constructor(
        subtotal: number, tax: number, total: number, fees: { title: string, value: number }[], discounts: { title: string, value: number }[],
        cartItems: Map<string, CartItem>, usedCouponCode?:string, couponDiscountTitle?: string, percentageDiscount?: { title: string, percentage: number }, 
        rewardPointInfo?:CouponRewardPointInfo ) {

        this.subtotal = subtotal;
        this.tax = tax;
        this.total = total;
        this.restaurantFees = fees;
        this.discounts = discounts;
        this.cartItems = cartItems;

        this.usedCouponCode = usedCouponCode;
        this.couponDiscountTitle = couponDiscountTitle;
        this.percentageDiscount = percentageDiscount;
        this.rewardPointInfo = rewardPointInfo;
    }

    clear() {
        this.subtotal = 0;
        this.tax = 0;
        this.total = 0;
        this.restaurantFees = [];
        this.discounts = [];
        this.cartItems = new Map();
        this.usedCouponCode = undefined;
        this.couponDiscountTitle = undefined;
        this.percentageDiscount = undefined;
        this.rewardPointInfo = undefined
    }

    setItemInCart(cartItemId: string, addedItem: CartItem, isDelivery: boolean) {
        this.cartItems.set(cartItemId, addedItem);
        this.recalculateTotals(isDelivery);
    }
    removeItemFromCart(cartItemId: string[], isDelivery: boolean) {
        cartItemId.forEach(cartItem => {
            this.cartItems.delete(cartItem);
        })
        this.recalculateTotals(isDelivery);
    }
    checkIfItemsInsideOfCartAreStillValid(currentDateTimeInfo: CurrentDateTimeInfo, isDelivery: boolean): { itemTitle: string, itemId: string }[] {
        let invalidItems: { itemTitle: string, itemId: string }[] = [];
        this.cartItems.forEach(item => {
            let parentIsInvalid = false;
            if (item.parentCategoryDisplayOptions != null) {
                const isParentItemValid = item.parentCategoryDisplayOptions.determineDisplayConditions(currentDateTimeInfo);
                if ((isDelivery && !isParentItemValid.displayForDelivery) || (!isDelivery && !isParentItemValid.displayForPickup)) {
                    invalidItems.push({ itemTitle: item.title, itemId: item.cartItemId })
                    parentIsInvalid = true;
                }
            }
            //If the parent is invalid, we dont need to execute add this item to the response array again...
            if (!parentIsInvalid && item.displayOptions != null) {
                const isItemValid = item.displayOptions.determineDisplayConditions(currentDateTimeInfo);
                if ((isDelivery && !isItemValid.displayForDelivery) || (!isDelivery && !isItemValid.displayForPickup)) {
                    invalidItems.push({ itemTitle: item.title, itemId: item.cartItemId })
                }
            }
        })
        return invalidItems;
    }

    /** Recalculate the values in this cart */
    recalculateTotals(isDelivery: boolean) {
        BigNumber.config({ DECIMAL_PLACES: 2 })
        //Im using bignumber here because javascript is imprecise and stupid
        let calculatedSubtotal = new BigNumber(0);
        this.cartItems.forEach((cartItem) => {
            calculatedSubtotal = calculatedSubtotal.plus(isDelivery ? cartItem.deliveryPrice : cartItem.price);
        })

        let feeTotal = new BigNumber(0);
        let deliveryChargeFee = new BigNumber(0);
        this.restaurantFees.forEach((fee) => {
            feeTotal = feeTotal.plus(fee.value);
            if(fee.title.toLowerCase() == "delivery charge"){
                deliveryChargeFee = deliveryChargeFee.plus(fee.value)
            }
        })

        let discountTotal = new BigNumber(0);
        this.discounts.forEach((discount) => {
            //If we have a percentage discount, we recalculate it before calculating the total
            if (this.percentageDiscount != undefined && this.percentageDiscount.title == discount.title) {
                discount.value = this.calculatePercentageDiscount(this.percentageDiscount.percentage);
            }

            discountTotal = discountTotal.plus(discount.value);
        })

        this.subtotal = calculatedSubtotal.decimalPlaces(2).toNumber();
        let subtotalPlusDelChg = calculatedSubtotal.plus(deliveryChargeFee);
        this.tax = subtotalPlusDelChg.times(0.13).decimalPlaces(2).toNumber(); //Ontario tax = 13%
        this.total = calculatedSubtotal.plus(this.tax).plus(feeTotal).minus(discountTotal).decimalPlaces(2).toNumber();
        if(this.total<0){
            this.total = 0;
        }
    }

    toAwsObject() {
        let cartObj: any = {
            "subtotal": this.subtotal,
            "tax": this.tax,
            "total": this.total,
            "restaurantFees": {},
            "discounts": {},
            "cartItems": {},
        }
        if (this.usedCouponCode != null) {
            cartObj["usedCouponCode"] = this.usedCouponCode;
        }
        if (this.couponDiscountTitle != null) {
            cartObj["couponDiscountTitle"] = this.couponDiscountTitle;
        }
        if (this.percentageDiscount != null) {
            cartObj["couponDiscountPercentage"] = this.percentageDiscount;
        }
        if (this.rewardPointInfo != null){
            cartObj["rewardPointInfo"] = this.rewardPointInfo.toAwsObject();
        }

        this.cartItems.forEach((cItem, cartItemKey) => {
            cartObj["cartItems"][cartItemKey] = cItem.toAwsObject();
        });
        this.restaurantFees.forEach((fee, feeTitle) => {
            cartObj["restaurantFees"][feeTitle] = fee;
        });
        this.discounts.forEach((discount, discountTitle) => {
            cartObj["discounts"][discountTitle] = discount;
        });

        return cartObj;
    }

    /**
     * Add a fee to the cart. Replace it if it already exists
     * @param feeName Name of the fee
     * @param feeValue How much it costs
     */
    setFee(feeName: string, feeValue: number, isDelivery: boolean, dontRecalculateTotalsAfterwards?:boolean) {
        let feeAdded = false;
        const newFee = { title: feeName, value: feeValue };

        //Search for the fees currently inside of the array with the same name as the one we want to add
        //Replaces them if they exist
        for (let i = 0; i < this.restaurantFees.length; i++) {
            if (this.restaurantFees[i].title == newFee.title) {
                this.restaurantFees[i] = newFee;
                feeAdded = true;
                break;
            }
        }
        //If the fee is not in the array, we add it.
        if (!feeAdded) {
            this.restaurantFees.push(newFee);
        }

        if(!dontRecalculateTotalsAfterwards){
            this.recalculateTotals(isDelivery);
        }
    }

    /**
     * Remove a fee from the cart.
     * @param feeName Name of the fee
     */
    removeFee(feeName: string, isDelivery: boolean) {
        //Search for the fees currently inside of the array with the same name as the one we want to remove
        for (let i = 0; i < this.restaurantFees.length; i++) {
            if (this.restaurantFees[i].title == feeName) {
                this.restaurantFees.splice(i,1);
                break;
            }
        }
        this.recalculateTotals(isDelivery);
    }

    /**
     * Removes all fees on the cart, except the tip
     * @param isDelivery 
     * @param dontRecalculateTotalsAfterwards 
     */
    removeAllRestaurantFees(isDelivery: boolean, dontRecalculateTotalsAfterwards?:boolean){
        let tipFee: { title: string, value: number } | null = null;
        
        for (let i = 0; i < this.restaurantFees.length; i++) {
            if (this.restaurantFees[i].title == CartObject.TIP_FEE_NAME) {
                tipFee = this.restaurantFees[i];
                break;
            }
        }
        
        this.restaurantFees = [];
        if(tipFee!=null){
            this.restaurantFees.push(tipFee);
        }

        if(!dontRecalculateTotalsAfterwards){
            this.recalculateTotals(isDelivery);
        }
    }

    /**
     * Add a fee to the cart. Replace it if it already exists
     * @param feeName Name of the fee
     */
    removeCoupon(isDelivery: boolean) {
        if(this.couponDiscountTitle!=null && this.couponDiscountTitle!=''){
            if(this.percentageDiscount!=null && this.percentageDiscount.title == this.couponDiscountTitle){
                this.percentageDiscount = undefined;
            }
            this.usedCouponCode = undefined;
            this.removeDiscount(this.couponDiscountTitle, isDelivery)
            this.couponDiscountTitle = undefined;
            this.rewardPointInfo = undefined;
        }
    }

    /**
     * Removes a discount from the cart
     * @param discountName Name of the discount
     */
    removeDiscount(discountName: string,isDelivery: boolean) {
        if(this.couponDiscountTitle!=null){
            //Search for the discounts currently inside of the array with the same name as the one we want to remove
            for (let i = 0; i < this.discounts.length; i++) {
                if (this.discounts[i].title == discountName) {
                    this.discounts.splice(i,1);
                    break;
                }
            }
            this.recalculateTotals(isDelivery);
        }
    }


    /**
     * Sets a discount for this cart based on a percentage.
     * Only one discount based on percentages can be added at a time.
     * @param discountName Name of the discount being set
     * @param percentage percentage of the discount ("10", for example, means "10% of your subtotal off")
     * @param isDelivery if the order is a delivery order
     * @param couponCode if this discount comes from a coupon, we set this. Only one coupon discount can be set at a time.
     */
    setPercentageDiscount(discountName: string, percentage: number, isDelivery: boolean, couponCode?: string, couponPointsInfo?:CouponRewardPointInfo) {
        this.percentageDiscount = { title: discountName, percentage: percentage };
        const calculatedValue = this.calculatePercentageDiscount(percentage);
        this.setFlatDiscount(discountName, calculatedValue, isDelivery, couponCode, couponPointsInfo, true)
    }

    /**
     * Add a discount to the cart. Replace it if it already exists
     * @param discountName Name of the discount
     * @param discountValue How much it costs
     */
    setFlatDiscount(discountName: string, discountValue: number, isDelivery: boolean, couponCode?: string, couponPointsInfo?:CouponRewardPointInfo, thePercentageDiscountMethodIsCallingThis?:boolean) {
        if(!thePercentageDiscountMethodIsCallingThis){
            this.percentageDiscount = undefined;    
        }

        // Users can only have one coupon discount at a time. If we already have one set, we remove it.
        if (couponCode!=null) {
            //We already have one. Remove it!
            if (this.couponDiscountTitle != undefined) {
                for (let i = 0; i < this.discounts.length; i++) {
                    if (this.discounts[i].title == this.couponDiscountTitle) {
                        this.discounts.splice(i, 1);
                    }
                }
            }
            //Sets a new value for our coupon discountName
            this.usedCouponCode = couponCode;
            this.couponDiscountTitle = discountName;
            this.rewardPointInfo = couponPointsInfo;
        }
        let discountAdded = false;
        const newDiscount = { title: discountName, value: discountValue };

        //Search for the discounts currently inside of the array with the same name as the one we want to add
        //Replaces them if they exist
        for (let i = 0; i < this.discounts.length; i++) {
            if (this.discounts[i].title == newDiscount.title) {
                this.discounts[i] = newDiscount;
                discountAdded = true;
                break;
            }
        }
        //If the discount is not in the array, we add it.
        if (!discountAdded) {
            this.discounts.push(newDiscount);
        }

        this.recalculateTotals(isDelivery);
    }

    private calculatePercentageDiscount(percentage: number) {
        return new BigNumber(this.subtotal).times(
            (new BigNumber(percentage).div(100))
        ).decimalPlaces(2).toNumber();
    }

    clearDiscounts(isDelivery: boolean) {
        this.usedCouponCode = undefined;
        this.couponDiscountTitle = undefined;
        this.percentageDiscount = undefined;
        this.rewardPointInfo = undefined;
        this.discounts = [];
        this.recalculateTotals(isDelivery);
    }

    static newFromAws(awsCartData: any): CartObject {
        if (awsCartData != null) {
            const cartItemsMap: Map<string, CartItem> = new Map();
            Object.keys(awsCartData.cartItems).forEach(key => {
                cartItemsMap.set(key, CartItem.newFromAws(awsCartData.cartItems[key]));
            });

            const restaurantFeesList: { title: string, value: number }[] = [];
            Object.keys(awsCartData.restaurantFees).forEach((key) => {
                restaurantFeesList.push(awsCartData.restaurantFees[key]);
            });

            const restaurantDiscountList: { title: string, value: number }[] = [];
            Object.keys(awsCartData.discounts).forEach((key) => {
                restaurantDiscountList.push(awsCartData.discounts[key]);
            });

            return new CartObject(
                awsCartData.subtotal,
                awsCartData.tax,
                awsCartData.total,
                restaurantFeesList,
                restaurantDiscountList,
                cartItemsMap,
                awsCartData.usedCouponCode,
                awsCartData.couponDiscountTitle,
                awsCartData.couponDiscountPercentage,
                CouponRewardPointInfo.newFromAws(awsCartData.rewardPointInfo)
            );
        } else {
            return this.generateNewCartObj();
        }
    }

    static generateNewCartObj() {
        return new CartObject(0, 0, 0, [], [], new Map());
    }
}


export class CouponRewardPointInfo{
    pointsCost:number;
    customerId:string;

    constructor(cost:number, customerId:string){
        this.pointsCost = cost;
        this.customerId = customerId;
    }

    toAwsObject(){
        return {
            "pointsCost":this.pointsCost,
            "customerId":this.customerId
        }
    }

    static newFromAws(awsData:any):CouponRewardPointInfo | undefined{
        if(awsData!=null){
            return new CouponRewardPointInfo(awsData.pointsCost,awsData.customerId)
        }else{
            return undefined
        }
    }
}